import React from "react";
import { Box, FormControl, Grid, Link, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link as RouterLink } from "react-router-dom";

import { getAuthenticationPasswordRequestSchema, type AuthenticationPasswordRequestData } from "../../../schemas";
import { ApplicationWordArt, AuthenticationButton, StyledFormLabel, StyledTextField } from "../../../components";

export interface LoginPasswordFormProps {
	readonly onSubmit: (data: AuthenticationPasswordRequestData) => void;
	readonly defaultUsername: string;
	readonly isError: boolean;
	readonly isSsoError: boolean;
}

export const LoginPasswordForm: React.FC<LoginPasswordFormProps> = ({
	onSubmit,
	defaultUsername,
	isError,
	isSsoError,
}) => {
	const intl = useIntl();

	const {
		control,
		register,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm<AuthenticationPasswordRequestData>({
		resolver: zodResolver(getAuthenticationPasswordRequestSchema(intl)),
		defaultValues: {
			username: defaultUsername,
		},
	});

	const currentUsername = useWatch({ control, name: "username" });

	return (
		<Grid container component="form" onSubmit={handleSubmit(onSubmit)} direction="column" gap={6}>
			<Box sx={{ margin: "auto" }}>
				<ApplicationWordArt width={300} />
			</Box>

			<Grid container direction="column" alignItems="center">
				<Typography variant="h1" color="white">
					<FormattedMessage id="login-password.title" defaultMessage="Sign in" />
				</Typography>
				<Typography color="white">
					<FormattedMessage id="login-password.subtitle" defaultMessage="Enter your password to continue" />
				</Typography>
			</Grid>

			<Grid container direction="column" gap={2}>
				<FormControl error={isError}>
					<StyledFormLabel color="white">
						<FormattedMessage id="login-password.email" defaultMessage="Email" />
					</StyledFormLabel>
					<StyledTextField
						placeholder={intl.formatMessage({
							id: "login-password.email.placeholder",
							defaultMessage: "Enter your email address",
						})}
						fullWidth
						autoFocus={Boolean(defaultUsername) === false}
						error={isError || Boolean(errors.username)}
						helperText={errors.username?.message}
						{...register("username")}
						slotProps={{
							htmlInput: {
								"data-testid": "login-password__input__username",
							},
						}}
					/>
				</FormControl>

				<FormControl error={isError}>
					<StyledFormLabel color="white">
						<FormattedMessage id="login-password.password" defaultMessage="Password" />
					</StyledFormLabel>
					<StyledTextField
						placeholder={intl.formatMessage({
							id: "login-password.placeholder",
							defaultMessage: "Enter password",
						})}
						type="password"
						fullWidth
						autoFocus={Boolean(defaultUsername)}
						error={isError || isSsoError || Boolean(errors.password)}
						helperText={
							isSsoError
								? intl.formatMessage({
										id: "login-password.sso-not-enabled",
										defaultMessage: "Please enter your password.",
									})
								: errors.password?.message
						}
						{...register("password")}
						slotProps={{
							htmlInput: {
								"data-testid": "login-password__input__password",
							},
							formHelperText: {
								"data-testid": "login-password__text__password-helper",
							},
						}}
					/>
				</FormControl>

				<Link
					to={`/password-reset?username=${encodeURIComponent(currentUsername)}`}
					component={RouterLink}
					variant="body2"
					sx={{ alignSelf: "flex-end", color: "white" }}
				>
					<FormattedMessage id="login-password.forgot-password" defaultMessage="Forgot password?" />
				</Link>

				{Boolean(isError) && (
					<Typography color="error" data-testid="login-password__text__error">
						<FormattedMessage id="login-password.error" defaultMessage="Invalid username or password" />
					</Typography>
				)}

				<Grid container gap={1} direction="column">
					<AuthenticationButton
						variant="contained"
						type="submit"
						fullWidth
						size="large"
						loading={isSubmitting}
						data-testid="login-password__button__submit"
					>
						<FormattedMessage id="login-password.submit" defaultMessage="Login" />
					</AuthenticationButton>

					<Link
						to={`/login?username=${encodeURIComponent(currentUsername)}`}
						component={RouterLink}
						sx={{ alignSelf: "flex-end", color: "white" }}
					>
						<FormattedMessage id="login-password.back" defaultMessage="Back" />
					</Link>
				</Grid>
			</Grid>
		</Grid>
	);
};
